import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Camera, Package, ArrowLeft, LogOut, Loader2, Calendar, DollarSign, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientOrdersPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [preorders, setPreorders] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('client_token');
    const clientData = localStorage.getItem('client_data');
    
    if (!token) {
      navigate('/client/auth');
      return;
    }

    if (clientData) {
      setClient(JSON.parse(clientData));
    }

    fetchOrders(token);
  }, [navigate]);

  const fetchOrders = async (token) => {
    try {
      const response = await axios.get(`${API}/client/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Separate regular orders and pre-orders
      const regularOrders = response.data.filter(o => o.order_type !== 'pre-order');
      const preordersList = response.data.filter(o => o.order_type === 'pre-order');
      
      setOrders(regularOrders);
      setPreorders(preordersList);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('client_token');
        localStorage.removeItem('client_data');
        navigate('/client/auth');
      } else {
        toast.error('Failed to load orders');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_data');
    toast.success('Logged out successfully');
    navigate('/client/auth');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status, paymentStatus) => {
    if (paymentStatus === 'paid') {
      return <Badge className="bg-green-100 text-green-700">Paid</Badge>;
    }
    if (paymentStatus === 'pending_cash') {
      return <Badge className="bg-amber-100 text-amber-700">Cash - Pending</Badge>;
    }
    if (paymentStatus === 'pending_etransfer') {
      return <Badge className="bg-blue-100 text-blue-700">e-Transfer - Pending</Badge>;
    }
    return <Badge className="bg-stone-100 text-stone-600">{status}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-stone-500 hover:text-stone-700">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Camera className="h-6 w-6 text-amber-600" />
              <h1 className="text-xl font-bold text-stone-800">Bright Eyes Photography</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-stone-600">Hi, {client?.name?.split(' ')[0]}</span>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>

        {/* Page Title */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-stone-800">My Orders</h2>
          <p className="text-stone-600">View your order history and status</p>
        </div>

        {/* Pre-Orders Section */}
        {preorders.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-600" />
              Pre-Orders ({preorders.length})
            </h3>
            <div className="space-y-4">
              {preorders.map((order) => (
                <Card key={order.id} className="border-stone-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-stone-800">
                            {order.event_name || 'Pre-Order'}
                          </span>
                          {getStatusBadge(order.status, order.payment_status)}
                        </div>
                        {order.child_name && (
                          <p className="text-sm text-stone-600">For: {order.child_name}</p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-stone-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(order.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            {formatCurrency(order.total)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-amber-600">{formatCurrency(order.total)}</p>
                        <p className="text-xs text-stone-500">{order.items?.length || 0} items</p>
                      </div>
                    </div>
                    {/* Items Preview */}
                    <div className="mt-3 pt-3 border-t">
                      <div className="text-sm text-stone-600">
                        {order.items?.slice(0, 3).map((item, idx) => (
                          <span key={idx}>
                            {item.package_name || item.product_name}
                            {idx < Math.min(order.items.length, 3) - 1 ? ', ' : ''}
                          </span>
                        ))}
                        {order.items?.length > 3 && <span className="text-stone-400"> +{order.items.length - 3} more</span>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Regular Orders Section */}
        {orders.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
              <Camera className="h-5 w-5 text-amber-600" />
              Gallery Orders ({orders.length})
            </h3>
            <div className="space-y-4">
              {orders.map((order) => (
                <Card key={order.id} className="border-stone-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-stone-800">Order #{order.id.slice(0, 8).toUpperCase()}</span>
                          {getStatusBadge(order.status, order.payment_status)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-stone-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(order.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign className="h-3 w-3" />
                            {formatCurrency(order.total)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-amber-600">{formatCurrency(order.total)}</p>
                        <p className="text-xs text-stone-500">{order.items?.length || 0} items</p>
                      </div>
                    </div>
                    {/* Items Preview */}
                    <div className="mt-3 pt-3 border-t">
                      <div className="text-sm text-stone-600">
                        {order.items?.slice(0, 3).map((item, idx) => (
                          <span key={idx}>
                            {item.package?.name || item.product?.name || 'Item'}
                            {idx < Math.min(order.items.length, 3) - 1 ? ', ' : ''}
                          </span>
                        ))}
                        {order.items?.length > 3 && <span className="text-stone-400"> +{order.items.length - 3} more</span>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {orders.length === 0 && preorders.length === 0 && (
          <Card className="border-stone-200">
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto text-stone-300 mb-4" />
              <h3 className="text-lg font-semibold text-stone-800 mb-2">No Orders Yet</h3>
              <p className="text-stone-600 mb-4">
                You haven't placed any orders yet. Browse galleries to get started!
              </p>
              <Link to="/">
                <Button className="bg-amber-500 hover:bg-amber-600">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Browse Galleries
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ClientOrdersPage;
